import { createReducer, on } from '@ngrx/store';
import { AuthState, initialAuthState } from './auth.state';
import * as AuthActions from './auth.actions';

/**
 * Reducer semplificato - gestisce solo lo stato di autenticazione
 */
export const authReducer = createReducer(
  initialAuthState,

  // Auth State Changed (da Firebase listener)
  on(AuthActions.authStateChanged, (state, { firebaseUser }) => {
    // Se c'è un firebaseUser ma non abbiamo ancora l'utente completo, mettiamo isLoading: true
    // perché sappiamo che loadBackendUserData verrà triggerato subito dopo
    const shouldLoad = firebaseUser !== null && state.user === null;
    
    return {
      ...state,
      firebaseUser,
      // Se Firebase dice che non c'è utente, pulisci tutto
      user: firebaseUser === null ? null : state.user,
      isInitialized: true,
      isLoading: shouldLoad, // Importante: inizia il loading se dobbiamo caricare i dati backend
      error: null
    };
  }),

  // Login
  on(AuthActions.login, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.loginSuccess, (state, { firebaseUser }) => ({
    ...state,
    firebaseUser,
    isLoading: false,
    error: null
  })),

  on(AuthActions.loginFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Login with Google
  on(AuthActions.loginWithGoogle, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.loginWithGoogleSuccess, (state, { firebaseUser }) => ({
    ...state,
    firebaseUser,
    isLoading: false,
    error: null
  })),

  on(AuthActions.loginWithGoogleFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Register
  on(AuthActions.register, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.registerSuccess, (state, { firebaseUser }) => ({
    ...state,
    firebaseUser,
    isLoading: false,
    error: null
  })),

  on(AuthActions.registerFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Logout
  on(AuthActions.logout, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.logoutSuccess, () => ({
    ...initialAuthState,
    isInitialized: true // Mantieni l'inizializzazione anche dopo logout
  })),

  on(AuthActions.logoutSuccessWithError, (state, { error }) => ({
    ...initialAuthState,
    isInitialized: true, // Mantieni l'inizializzazione anche dopo logout
    error // Mantieni l'errore per mostrarlo nella pagina di login
  })),

  on(AuthActions.logoutFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Load Backend User Data
  on(AuthActions.loadBackendUserData, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.loadBackendUserDataSuccess, (state, { user }) => {
    if (!state.firebaseUser) {
      return {
        ...state,
        isLoading: false,
        error: null
      };
    }

    // Combina i dati backend con i dati Firebase
    return {
      ...state,
      user: {
        ...user,
        firebaseUser: state.firebaseUser
      },
      isLoading: false,
      error: null
    };
  }),

  on(AuthActions.loadBackendUserDataFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Create Backend User (per login Google/OAuth)
  on(AuthActions.createBackendUser, (state) => ({
    ...state,
    isLoading: true,
    error: null
  })),

  on(AuthActions.createBackendUserSuccess, (state, { user }) => {
    if (!state.firebaseUser) {
      return {
        ...state,
        isLoading: false,
        error: null
      };
    }

    // Combina i dati backend con i dati Firebase
    return {
      ...state,
      user: {
        ...user,
        firebaseUser: state.firebaseUser
      },
      isLoading: false,
      error: null
    };
  }),

  on(AuthActions.createBackendUserFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error
  })),

  // Error Management
  on(AuthActions.clearError, (state) => ({
    ...state,
    error: null
  }))
);
